import { useEffect, useState } from "react";
import classes from "./BookingsManagement.module.css";
import { useParams } from "react-router-dom";
import CommonCard from "../../components/ui/CommonCard/CommonCard";
import { MdOutlineEmail, MdOutlineLocalPhone } from "react-icons/md";
import axios from "axios";
import { env } from "../../constants/env";
import { toast } from "react-toastify";

const statusOptions = [
  { value: "Pending", label: "Mark as pending" },
  { value: "Confirmed", label: "Mark as confirmed" },
  { value: "Cancelled", label: "Mark as cancelled" },
  { value: "Completed", label: "Mark as completed" },
];

const statusStyles = {
  Pending: { color: "#AD4001", backgroundColor: "#FFC796" },
  Cancelled: { color: "#AD0101", backgroundColor: "#F7D4D4" },
  Completed: { color: "#12AD01", backgroundColor: "#D8F7D4" },
  Confirmed: { color: "#0149AD", backgroundColor: "#D4E3F7" },
};

const BookingsManagement = () => {
  const { id } = useParams();
  const [booking, setBooking] = useState(null);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [loading, setLoading] = useState(true);
  const [updatingStatus, setUpdatingStatus] = useState(false);

  useEffect(() => {
    const fetchBooking = async () => {
      try {
        const res = await axios.get(`${env.apiUrl}/api/bookings/${id}`);
        setBooking(res.data);
        setSelectedStatus(res.data.status);
      } catch (err) {
        console.error("Failed to fetch booking:", err);
      } finally {
        setLoading(false);
      }
    };

    if (id) fetchBooking();
  }, [id]);

  const handleStatusChange = async (e) => {
    const newStatus = e.target.value;
    setUpdatingStatus(true);

    try {
      await axios.patch(`${env.apiUrl}/api/bookings/${id}/status`, {
        status: newStatus,
      });
      setSelectedStatus(newStatus);
      toast.success(`Status updated to "${newStatus}"`);
    } catch (err) {
      const message =
        err.response?.data?.message ||
        err.message ||
        "Something went wrong while updating status.";
      console.error("Failed to update status:", err);
      toast.error(message);
    } finally {
      setUpdatingStatus(false);
    }
  };

  if (loading || !booking) return <div>Loading booking details...</div>;

  return (
    <div className={classes.bookingsManagement}>
      <div
        className={`${classes.headCard} d-flex flex-column flex-md-row justify-content-between align-items-start align-items-md-center gap-3 mb-3`}
      >
        <div className={classes.cardRevenueHeader}>
          <h5>Booking: {id.toString().slice(-5).toUpperCase()}</h5>
        </div>
        <div className={classes.statusSelector}>
          <div className="position-relative">
            <div
              className={`form-select ${classes.customSelect}`}
              style={statusStyles[selectedStatus]}
            >
              {selectedStatus}
              {updatingStatus && <span className={classes.spinner}></span>}
            </div>
            <select
              className="form-select position-absolute top-0 start-0 opacity-0"
              value={selectedStatus}
              onChange={handleStatusChange}
            >
              {statusOptions.map((status, i) => (
                <option key={i} value={status.value}>
                  {status.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      <div className="row">
        {/* Customer Info */}
        <div className="col-md-6">
          <CommonCard className={classes.bookingDetails}>
            <div className={classes.userDetails}>
              <h3>Customer information</h3>
              <div
                className={`${classes.userInfo} d-flex align-items-center gap-2 my-3`}
              >
                <div
                  className={classes.avatarContainer}
                  style={{
                    backgroundImage: `url(${
                      booking.customer.avatar || "/user.jpg"
                    })`,
                  }}
                />
                <div>
                  <p className={classes.userName}>{booking.customer.name}</p>
                  <p className={classes.userId}>
                    #{booking.customer.id.slice(-5).toUpperCase()}
                  </p>
                </div>
              </div>
              <div
                className={`${classes.userInfo} d-flex align-items-center gap-2 mb-3`}
              >
                <MdOutlineEmail size={17} />
                <p>{booking.customer.email}</p>
              </div>
              <div
                className={`${classes.userInfo} d-flex align-items-center gap-2`}
              >
                <MdOutlineLocalPhone size={17} />
                <p>{booking.customer.phoneNumber || "N/A"}</p>
              </div>
            </div>
          </CommonCard>

          {/* Payment Info */}
          <CommonCard className={`mt-3 ${classes.customerCard}`}>
            <div className={`d-flex flex-column ${classes.carInfo}`}>
              <h3>Payment info</h3>
            </div>
            <div className={`d-flex flex-column ${classes.carDetails}`}>
              <div className="d-flex align-items-center justify-content-between">
                <h4>Base price</h4>
                <p>K{booking.payment.base.toFixed(2)}</p>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <h4>Insurance ({booking.car.insurancePercentage}%)</h4>
                <p>K{booking.payment.insurance.toFixed(2)}</p>
              </div>
              <div
                className={`d-flex align-items-center justify-content-between ${classes.total}`}
              >
                <p>Total</p>
                <p>K{booking.payment.total.toFixed(2)}</p>
              </div>
            </div>
          </CommonCard>
        </div>

        {/* Car Info & Period */}
        <div className="col-md-6">
          <CommonCard className={classes.customerCard}>
            <div className={`d-flex flex-column ${classes.carInfo}`}>
              <h3>Car details</h3>
              <div>
                <p className={classes.carName}>{booking.car.name}</p>
                <p className={classes.carId}>
                  #{booking.car.id.slice(-5).toUpperCase()}
                </p>
              </div>
            </div>
            <div className={`d-flex flex-column ${classes.carDetails}`}>
              <div className="d-flex align-items-center justify-content-between">
                <h4>Type</h4>
                <p>{booking.car.type}</p>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <h4>Fuel</h4>
                <p>{booking.car.fuel}</p>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <h4>Transmission</h4>
                <p>{booking.car.transmission}</p>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <h4>Seats</h4>
                <p>{booking.car.seats}</p>
              </div>
            </div>
          </CommonCard>

          {/* Period Info */}
          <CommonCard className={`mt-3 ${classes.customerCard}`}>
            <div className={`d-flex flex-column ${classes.carInfo}`}>
              <h3>Period</h3>
            </div>
            <div className={`d-flex flex-column ${classes.carDetails}`}>
              <div className="d-flex align-items-center justify-content-between">
                <h4>Start date</h4>
                <p>{new Date(booking.period.startDate).toDateString()}</p>
              </div>
              <div className="d-flex align-items-center justify-content-between">
                <h4>End date</h4>
                <p>{new Date(booking.period.endDate).toDateString()}</p>
              </div>
            </div>
          </CommonCard>
        </div>
      </div>
    </div>
  );
};

export default BookingsManagement;
